import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QuizFormFields } from '@/components/question/forms/quiz/QuizForm';
import {
  IsNewItemArgs,
  UseQuestionFieldArrayReturn,
} from '@/data/hook/question/answers/types';
import { useSpotlight } from '@/data/hook/shared/useSpotlight';
import { QuizAnswerCreateDTO } from '@/models/Answer';
import { validateQuizAnswers } from '@/utils/questions/validateAnswers';

type UseQuizAnswersReturn = UseQuestionFieldArrayReturn<QuizAnswerCreateDTO> & {
  handleToggleCorrect: (index: number) => void;
};

export function useQuizAnswers(): UseQuizAnswersReturn {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  const { control, getValues } = useFormContext<QuizFormFields>();
  const {
    fieldState: { error },
  } = useController({ control, name: 'quizAnswers' });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'quizAnswers',
    keyName: 'fieldId',
    rules: {
      validate: (answers = []) => validateQuizAnswers(answers, t),
    },
  });

  const { focus, clear, clearIfMatches, isFocused } = useSpotlight();

  const handleAdd = () => {
    append({ text: t('newOption'), isCorrect: false });
    focus(fields.length);
  };

  const handleUpdateText = (index: number, text: string) => {
    const current = getValues(`quizAnswers.${index}`);
    update(index, { ...current, text, mediaFile: null, altText: '' });
    clearIfMatches(index);
  };

  const handleUpdateMedia = (
    index: number,
    mediaFile: File | string,
    altText?: string,
  ) => {
    const current = getValues(`quizAnswers.${index}`);
    update(index, {
      ...current,
      mediaFile,
      altText: altText ?? null,
      text: '',
    });
    clearIfMatches(index);
  };

  const handleToggleCorrect = (index: number) => {
    const current = getValues(`quizAnswers.${index}`);
    update(index, { ...current, isCorrect: !current.isCorrect });
    clearIfMatches(index);
  };

  const handleDelete = (index: number) => {
    remove(index);
    clear();
  };

  const isNewItem = ({ index }: IsNewItemArgs) => isFocused(index);

  return {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleToggleCorrect,
    handleDelete,
    isNewItem,
  };
}
